import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../shared/api/client';
import { PostReactions } from './PostReactions';
import { PostReplies } from './PostReplies';
import { PostForm } from './PostForm';
import { CorroborationSection } from './CorroborationSection';
import { linkifyText } from './utils/linkify';

interface PostCardProps {
  post: any;
  onUpdated: (post: any) => void;
  onDeleted: (postId: string) => void;
}

export function PostCard({ post, onUpdated, onDeleted }: PostCardProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [replyCount, setReplyCount] = useState(post.replies?.length || 0);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ url: string; name: string } | null>(null);
  const [isTogglingAI, setIsTogglingAI] = useState(false);

  // X/Twitter-style: truncate at ~280 characters
  const TRUNCATE_LENGTH = 280;
  const shouldTruncate = post.text.length > TRUNCATE_LENGTH;

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxImage]);

  const handleDelete = async () => {
    if (!confirm('Delete this journal entry?')) return;

    setIsDeleting(true);
    try {
      await apiClient.deleteAIContextPost(post.id);
      onDeleted(post.id);
    } catch (err) {
      alert('Failed to delete post');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!confirm('Delete this file?')) return;

    setDeletingAttachmentId(attachmentId);
    try {
      await apiClient.removeAIContextAttachment(post.id, attachmentId);

      // Update post to remove the attachment
      const updatedPost = {
        ...post,
        attachments: post.attachments.filter((a: any) => a.id !== attachmentId),
      };
      onUpdated(updatedPost);
    } catch (err) {
      alert('Failed to delete attachment');
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleToggleAI = async () => {
    setIsTogglingAI(true);
    try {
      const newIncludeInAI = !post.includeInAI;
      await apiClient.updateAIContextPost(post.id, undefined, undefined, newIncludeInAI, undefined);

      // Update post with new includeInAI status
      const updatedPost = {
        ...post,
        includeInAI: newIncludeInAI,
      };
      onUpdated(updatedPost);
    } catch (err) {
      alert('Failed to update AI context setting');
    } finally {
      setIsTogglingAI(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return formatDateTime(dateString);
  };

  if (isEditing) {
    return (
      <PostForm
        initialPost={post}
        postId={post.id}
        onPostCreated={(updatedPost) => {
          onUpdated(updatedPost);
          setIsEditing(false);
        }}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="card bg-base-100 shadow hover:shadow-lg transition-shadow">
      <div className="card-body">
        {/* Header */}
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1">
            <p className="text-sm text-base-content/60">
              📅 {formatDate(post.publishedAt)}
            </p>
            {post.updatedAt && post.publishedAt !== post.updatedAt && (
              <p className="text-xs text-base-content/40 mt-0.5">
                edited {getRelativeTime(post.updatedAt)}
              </p>
            )}
          </div>
          <div className="flex gap-1">
            {!post.includeInAI && (
              <div className="badge badge-warning gap-1 text-xs">
                🔒 Hidden from AI
              </div>
            )}
            {post.isPublic && (
              <div className="badge badge-secondary gap-1 text-xs">
                🌐 Public
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="text-base leading-relaxed">
          <p className="whitespace-pre-wrap">
            {shouldTruncate && !isExpanded
              ? linkifyText(post.text.substring(0, TRUNCATE_LENGTH) + '...')
              : linkifyText(post.text)}
          </p>
          {shouldTruncate && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-primary hover:underline text-sm mt-1 font-medium"
            >
              {isExpanded ? '← Show less' : 'Show more →'}
            </button>
          )}
        </div>

        {/* Attachments */}
        {post.attachments && post.attachments.length > 0 && (
          <div className="mt-3 divider my-2"></div>
        )}
        {post.attachments?.map((attachment: any) => {
          const isImage = attachment.fileType.startsWith('image/');
          const isPdf = attachment.fileType === 'application/pdf';
          const isDocument = ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/markdown', 'text/plain'].includes(attachment.fileType);
          const isDeleting = deletingAttachmentId === attachment.id;

          return (
            <div
              key={attachment.id}
              className="attachment mb-2 p-3 bg-base-200 rounded"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  {isImage ? (
                    <div className="flex flex-col gap-2">
                      <img
                        src={attachment.fileUrl}
                        alt={attachment.fileName}
                        className="max-h-64 rounded cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setLightboxImage({ url: attachment.fileUrl, name: attachment.fileName })}
                        title="Click to view full size"
                      />
                      <span className="text-xs text-base-content/60">{attachment.fileName}</span>
                    </div>
                  ) : isPdf ? (
                    <a
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link gap-2 flex items-center"
                    >
                      📄 {attachment.fileName}
                    </a>
                  ) : isDocument ? (
                    <a
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link gap-2 flex items-center"
                    >
                      📝 {attachment.fileName}
                    </a>
                  ) : (
                    <a
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link gap-2 flex items-center"
                    >
                      📎 {attachment.fileName}
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteAttachment(attachment.id)}
                  disabled={isDeleting}
                  className="btn btn-ghost btn-sm btn-circle text-error hover:bg-error hover:text-error-content"
                  title="Delete file"
                >
                  {isDeleting ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : (
                    '🗑️'
                  )}
                </button>
              </div>
            </div>
          );
        })}

        {/* Resume Tags */}
        {post.resumeTags && post.resumeTags.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-base-content/60 mb-1">Tagged to resumes:</p>
            <div className="flex flex-wrap gap-2">
              {post.resumeTags.map((tag: any) => (
                <div key={tag.id} className="badge badge-outline">
                  {tag.resume.title}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Corroborations */}
        <CorroborationSection postId={post.id} />

        {/* Divider */}
        <div className="divider my-3"></div>

        {/* Reactions */}
        <PostReactions postId={post.id} reactions={post.reactions} onReactionUpdated={() => { }} />

        {/* Actions */}
        <div className="card-actions justify-between mt-3">
          <div className="flex gap-1">
            <button
              className="btn btn-ghost btn-sm gap-1"
              onClick={() => setShowReplies(!showReplies)}
            >
              💭 {replyCount} {t('ai_context.reflections')}
            </button>
            <button
              className={`btn btn-sm gap-1 ${post.includeInAI ? 'btn-ghost' : 'btn-warning'}`}
              onClick={handleToggleAI}
              disabled={isTogglingAI}
              title={post.includeInAI ? 'Hide from AI context' : 'Include in AI context'}
            >
              {isTogglingAI ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                <>
                  {post.includeInAI ? '🔓' : '🔒'} {post.includeInAI ? 'AI' : 'Hidden'}
                </>
              )}
            </button>
          </div>
          <div className="flex gap-1">
            <button className="btn btn-ghost btn-sm" onClick={() => setIsEditing(true)}>
              ✏️ {t('common.edit')}
            </button>
            <button
              className="btn btn-ghost btn-sm btn-error"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? '...' : '🗑️'}
            </button>
          </div>
        </div>

        {/* Replies Section */}
        {showReplies && (
          <PostReplies
            postId={post.id}
            onRepliesChanged={(newCount) => setReplyCount(newCount)}
          />
        )}
      </div>

      {/* Image Lightbox Modal - Rendered via Portal */}
      {lightboxImage && createPortal(
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 overflow-hidden"
          style={{ zIndex: 99999 }}
          onClick={() => setLightboxImage(null)}
        >
          {/* Close Button - Fixed at top right of screen */}
          <button
            className="fixed top-4 right-4 btn btn-circle btn-sm bg-white/10 border-white/20 text-white hover:bg-white/20"
            onClick={() => setLightboxImage(null)}
          >
            ✕
          </button>

          <div className="relative max-w-7xl max-h-full flex flex-col items-center gap-4">
            <img
              src={lightboxImage.url}
              alt={lightboxImage.name}
              className="max-w-full max-h-[85vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center text-sm">{lightboxImage.name}</p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
